import React, {useEffect, useRef, useState} from "react";
import {Controller, useFormContext, useWatch} from "react-hook-form";
import {
  StyledDocUploadContainer,
  StyledDocUploadPreviewContainer,
  StyledDocUploadPreviewWrapper,
  StyledDocUploadRiveWrapper,
  StyledDocUploadVideo,
  StyledDocUploadVideoWrapper,
  StyledDocUploadWrapper,
} from "./styles";
import {Button, Image, Modal, Space} from "antd";
import {DeleteOutlined, UploadOutlined} from "@ant-design/icons";
import {
  FormFieldError,
  FormFieldHelperText,
} from "../../../../../../design-system/form-field";
import {
  StyledCoverImagesItemImageWrapper,
  StyledCoverImagesItemWrapper,
} from "../../../../../../components/offering-form/general/cover-images/cover-images-item/styles";
import {StyledModalContentWrapper} from "../../../../../../components/calls/upcoming-calls/call-card/call-card.style";
import {
  CreateKycDocumentUploadStepDocumentField,
  CreateKycDocumentUploadStepFormValues,
} from "../schema-validation";
import {useRive, Layout, Fit, Alignment} from "@rive-app/react-canvas";

type CreateKycDocUploadFormFieldProps = {
  name: CreateKycDocumentUploadStepDocumentField;
  label: string;
  btnText: string;
  uploadInstruction: string;
  docFeedback: string;
  modalTitle: string;
  modalUploadInstruction: React.ReactNode;
  videoSrc: "Selfie + ID" | "ID" | "PoR";
  required?: boolean;
  defaultPreviewUrl?: string | null; // from backend
};

export const CreateKycDocUploadFormField = React.memo(
  ({
    name,
    label,
    btnText,
    docFeedback,
    uploadInstruction,
    modalTitle,
    modalUploadInstruction,
    videoSrc,
    required,
    defaultPreviewUrl,
  }: CreateKycDocUploadFormFieldProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const {
      control,
      formState: {errors},
      setValue,
    } = useFormContext<CreateKycDocumentUploadStepFormValues>();
    const inputRef = useRef<HTMLInputElement | null>(null);

    // ✅ Safe hook usage — outside of any callback
    const fileOrUrl = useWatch({control, name});
    const [previewUrl, setPreviewUrl] = useState<string | null>(
      defaultPreviewUrl ?? null
    );
    const errorMessage = errors[name]?.message;

    const {RiveComponent} = useRive({
      src: "/kyc_assets.riv",
      // animations: ["play"],
      layout: new Layout({
        fit: Fit.Contain, // Change to: rive.Fit.Contain, or Cover
        alignment: Alignment.Center,
      }),
      autoplay: true,
      artboard: videoSrc,
      stateMachines: "State Machine 1",
    });

    useEffect(() => {
      setValue(name, defaultPreviewUrl ?? null);
    }, [defaultPreviewUrl, name, setValue]);

    useEffect(() => {
      if (!fileOrUrl) {
        setPreviewUrl(null);
        return;
      }
      if (typeof fileOrUrl === "string") {
        setPreviewUrl(fileOrUrl);
      } else {
        const url = URL.createObjectURL(fileOrUrl);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
      }
    }, [fileOrUrl]);

    return (
      <div>
        <Space size={4} direction="vertical" style={{display: "flex"}}>
          <p>{label}</p>

          <Controller
            name={name}
            control={control}
            rules={{required}}
            render={({field: {onChange}}) => (
              <div>
                {previewUrl ? (
                  <StyledDocUploadPreviewWrapper>
                    <StyledDocUploadPreviewContainer>
                      <StyledCoverImagesItemWrapper>
                        <StyledCoverImagesItemImageWrapper>
                          <Image
                            style={{
                              borderRadius: "4px",
                              objectFit: "cover",
                            }}
                            width={"100%"}
                            height={"100%"}
                            src={previewUrl}
                            alt={`${name} preview`}
                          />
                        </StyledCoverImagesItemImageWrapper>
                        <div>
                          <Button
                            onClick={() => onChange(null)}
                            size="small"
                            type="text"
                            icon={<DeleteOutlined />}
                          />
                        </div>
                      </StyledCoverImagesItemWrapper>
                    </StyledDocUploadPreviewContainer>
                  </StyledDocUploadPreviewWrapper>
                ) : (
                  <StyledDocUploadWrapper>
                    <StyledDocUploadContainer>
                      <Button
                        onClick={() => setIsModalOpen(true)}
                        size="small"
                        icon={<UploadOutlined />}
                      >
                        {btnText}
                      </Button>
                      <p>{uploadInstruction}</p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          onChange(file);
                          setIsModalOpen(false);
                        }}
                        ref={inputRef}
                        style={{display: "none"}}
                      />
                    </StyledDocUploadContainer>
                  </StyledDocUploadWrapper>
                )}
              </div>
            )}
          />
          <FormFieldError errorMessage={errorMessage} />
          <FormFieldHelperText text={docFeedback} />
        </Space>
        <Modal
          visible={isModalOpen}
          title={modalTitle}
          onOk={() => setIsModalOpen(false)}
          onCancel={() => setIsModalOpen(false)}
          footer={null}
          centered
        >
          <StyledModalContentWrapper>
            <Space size={24} direction="vertical" style={{display: "flex"}}>
              <Space size={16} direction="vertical" style={{display: "flex"}}>
                {/* <StyledDocUploadVideoWrapper>
                  <StyledDocUploadVideo
                  src={videoSrc}
                  autoPlay
                  loop
                  ></StyledDocUploadVideo>
                  </StyledDocUploadVideoWrapper> */}
                <StyledDocUploadVideoWrapper>
                  <StyledDocUploadRiveWrapper>
                    <RiveComponent />
                  </StyledDocUploadRiveWrapper>
                </StyledDocUploadVideoWrapper>
                <div>{modalUploadInstruction}</div>
              </Space>
              <Button
                size="small"
                type="primary"
                block
                onClick={() => {
                  if (inputRef.current) {
                    inputRef.current.click();
                  }
                }}
              >
                Proceed
              </Button>
            </Space>
          </StyledModalContentWrapper>
        </Modal>
      </div>
    );
  }
);
